import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  List,
  Tag,
  Button,
  Select,
  DatePicker,
  Space,
  Empty,
  Row,
  Col,
  Statistic,
} from 'antd';
import { PlusOutlined, BarChartOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { emotionApi } from '@tradermind/shared/api';
import type { EmotionFilters } from '@tradermind/shared/types';
import { formatRelativeTime } from '@tradermind/shared/utils';
import { EMOTION_TYPES, EMOTION_CONTEXTS, getEmotionById, getIntensityColor } from '@tradermind/shared/constants';

const { RangePicker } = DatePicker;

export default function EmotionListPage() {
  const { t } = useTranslation('emotion');
  const navigate = useNavigate();
  const [filters, setFilters] = useState<EmotionFilters>({});
  const [pagination, setPagination] = useState({ page: 1, page_size: 20 });

  const { data, isLoading } = useQuery({
    queryKey: ['emotions', filters, pagination],
    queryFn: () => emotionApi.getEmotionLogs({ ...filters, ...pagination }),
  });

  const { data: statsData } = useQuery({
    queryKey: ['emotions', 'stats'],
    queryFn: () => emotionApi.getEmotionStats(),
  });

  const emotions = data?.data?.items || [];
  const total = data?.data?.total || 0;
  const stats = statsData?.data;

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title={t('stats.total_logs')} value={stats?.total_logs || 0} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title={t('stats.average_intensity')}
              value={stats?.average_intensity || 0}
              precision={1}
              suffix="/ 10"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title={t('stats.most_common')}
              value={
                stats?.most_common_emotion
                  ? t(getEmotionById(stats.most_common_emotion)?.label || '')
                  : '-'
              }
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={t('title')}
        extra={
          <Space>
            <Button icon={<BarChartOutlined />} onClick={() => navigate('/emotions/analysis')}>
              {t('emotion_analysis')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/emotions/new')}>
              {t('record_emotion')}
            </Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Select
              placeholder={t('emotion_type')}
              allowClear
              style={{ width: 140 }}
              value={filters.emotion_type}
              onChange={(value) => setFilters({ ...filters, emotion_type: value })}
              options={EMOTION_TYPES.map((e) => ({
                value: e.id,
                label: `${e.emoji} ${t(e.label)}`,
              }))}
            />
            <Select
              placeholder={t('context')}
              allowClear
              style={{ width: 120 }}
              value={filters.context}
              onChange={(value) => setFilters({ ...filters, context: value })}
              options={EMOTION_CONTEXTS.map((c) => ({
                value: c.value,
                label: t(c.label),
              }))}
            />
            <RangePicker
              onChange={(dates) => {
                if (dates) {
                  setFilters({
                    ...filters,
                    start_date: dates[0]?.format('YYYY-MM-DD'),
                    end_date: dates[1]?.format('YYYY-MM-DD'),
                  });
                } else {
                  setFilters({ ...filters, start_date: undefined, end_date: undefined });
                }
              }}
            />
          </Space>
        </div>

        <List
          loading={isLoading}
          dataSource={emotions}
          locale={{ emptyText: <Empty description={t('no_records')} /> }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.page_size,
            total,
            onChange: (page, pageSize) => setPagination({ page, page_size: pageSize }),
          }}
          renderItem={(item) => {
            const emotionInfo = getEmotionById(item.emotion_type);
            const contextInfo = EMOTION_CONTEXTS.find((c) => c.value === item.context);

            return (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <div style={{ fontSize: 32 }}>{emotionInfo?.emoji}</div>
                  }
                  title={
                    <Space>
                      <Tag color={emotionInfo?.color}>{t(emotionInfo?.label || '')}</Tag>
                      <Tag>{t(contextInfo?.label || '')}</Tag>
                      <span
                        style={{
                          color: getIntensityColor(item.intensity),
                          fontWeight: 500,
                        }}
                      >
                        {t('intensity_label')}: {item.intensity}/10
                      </span>
                    </Space>
                  }
                  description={
                    <div>
                      <div>{item.notes || t('no_notes')}</div>
                      <div style={{ marginTop: 4, color: '#999' }}>
                        {formatRelativeTime(item.created_at)}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Card>
    </div>
  );
}
